import { Injectable } from '@angular/core';
import { Adapter } from '@core/adapter';
import { LearningPlanCourseCertificateListItem } from '@modules/organisation-workspaces/models/learning-plan-course';
import type { Organisation } from './organisation.model';

/**
 * Qualifications are what an organisation tracks for its members: certifications, knowledge areas
 * and skills. They belong to one organisation profile.
 *
 * - A `certification` never has levels, always requires a certificate, and can "cover" other
 *   certifications (holding it implies holding everything it covers, transitively).
 * - `knowledge` and `skill` have ordered numeric levels and no cover links.
 */
export type QualificationType = 'certification' | 'knowledge' | 'skill';
export type ExpirationUnit = 'day' | 'week' | 'month' | 'year';

export const QUALIFICATION_TYPES: QualificationType[] = ['certification', 'knowledge', 'skill'];

/** What a function or a team asks for, per type, as their collections count it. */
export type QualificationTypeCounts = Record<QualificationType, number>;

/**
 * The `qualificationCounts` of a collection payload, with every type present: a type the payload
 * leaves nothing out of asks for nothing. Read per type because the modules are sold per type —
 * what is set up for certifications says nothing about knowledge and skills.
 */
export function adaptQualificationCounts(raw: any): QualificationTypeCounts {
  const counts = raw ?? {};
  return QUALIFICATION_TYPES.reduce((all, type) => {
    all[type] = Number(counts[type] ?? 0);
    return all;
  }, {} as QualificationTypeCounts);
}

/** The same counts as one number, which is what a list showing only "how many" has always said. */
export function qualificationCountTotal(counts: QualificationTypeCounts): number {
  return QUALIFICATION_TYPES.reduce((total, type) => total + counts[type], 0);
}
export const EXPIRATION_UNITS: ExpirationUnit[] = ['day', 'week', 'month', 'year'];

/**
 * Qualification types are gated by two organisation modules, each behind its own feature:
 * certifications by the certification module, knowledge and skills by the qualification module.
 * Either can be used without the other. On top of the module sits what this member may do with the
 * type: read it, and manage it. Those rights are only consulted where the organisation has the
 * advanced access roles; without them everyone works with every module that is on, as before.
 */
const typeModule = (type: QualificationType) => type === 'certification'
  ? {
      feature: 'certification',
      enabled: (org: Organisation) => org.certificationModuleEnabled,
      read: (org: Organisation) => org.permissions?.canReadCertifications,
      manage: (org: Organisation) => org.permissions?.canManageCertifications,
    }
  : {
      feature: 'qualification',
      enabled: (org: Organisation) => org.qualificationModuleEnabled,
      read: (org: Organisation) => org.permissions?.canReadQualifications,
      manage: (org: Organisation) => org.permissions?.canManageQualifications,
    };

/** A right the organisation never sent is a right nobody is being held to. */
const granted = (value: boolean | undefined): boolean => value !== false;

/** Whether the organisation has the module for `type` switched on, and this member may read it. */
export const isQualificationTypeEnabled = (org: Organisation | null | undefined, type: QualificationType): boolean => {
  if (!org) return false;
  const module = typeModule(type);
  // Managing implies reading, whatever the stored read flag says.
  return !!module.enabled(org) && (granted(module.read(org)) || module.manage(org) === true);
};

/** Whether this member may create, change or delete qualifications of `type`. */
export const canManageQualificationType = (org: Organisation | null | undefined, type: QualificationType): boolean =>
  !!org && !!typeModule(type).enabled(org) && granted(typeModule(type).manage(org));

/**
 * Whether this member may write what a member holds of `type` — their levels, confirmations, growth
 * plans and personal requirements. Where the organisation has the advanced access roles that is the
 * manage right; where it does not, writing stays with the administrators, as before.
 */
export const canWriteQualificationType = (org: Organisation | null | undefined, type: QualificationType): boolean =>
  !!org && (org.hasAdvancedPermissionAccess ? canManageQualificationType(org, type) : !!org.admin);

/** The types whose member data this member may write. */
export const writableQualificationTypes = (org: Organisation | null | undefined): QualificationType[] =>
  QUALIFICATION_TYPES.filter(type => canWriteQualificationType(org, type));

/** The types this member may change; the catalogue itself stays administrator-only. */
export const manageableQualificationTypes = (org: Organisation | null | undefined): QualificationType[] =>
  QUALIFICATION_TYPES.filter(type => canManageQualificationType(org, type));

/** Whether the organisation's subscription includes the module for `type`, switched on or not. */
export const hasQualificationTypeAccess = (org: Organisation | null | undefined, type: QualificationType): boolean =>
  !!org && !!org.features.find(f => f.name === typeModule(type).feature)?.hasAccess;

/** The types the organisation can work with right now, in the usual order. */
export const enabledQualificationTypes = (org: Organisation | null | undefined): QualificationType[] =>
  QUALIFICATION_TYPES.filter(type => isQualificationTypeEnabled(org, type));

/** Icon + label colour per type, shared by every screen that renders a qualification chip. */
export const QUALIFICATION_TYPE_META: Record<QualificationType, { icon: string; color: string }> = {
  certification: { icon: 'approval', color: 'purple' },
  knowledge: { icon: 'menu_book', color: 'primary' },
  skill: { icon: 'handyman', color: 'accent' },
};

export interface QualificationLevel {
  id: string;
  // @id — the value requirements point at.
  iri: string;
  value: number;
  name: string | null;
  // Free text giving people more context on what the level means. On a qualification level this
  // is the qualification's own text ("knows the basic rules of basketball"); the scale's general
  // wording comes along as `scaleDescription`.
  description: string | null;
  // On a qualification level: IRI of the scale level it mirrors (value and name come from there).
  scaleLevel: string | null;
  scaleDescription: string | null;
}

export interface Qualification {
  id: string;
  // @id — the value cover links, requirements, courses and certificates point at.
  iri: string;
  organisationProfile: string;
  type: QualificationType;
  code: string;
  name: string;
  description: string | null;
  certificateRequired: boolean;
  // IRI of the scale a knowledge / skill is graded on; chosen on creation, fixed afterwards.
  scale: string | null;
  expirationValue: number | null;
  expirationUnit: ExpirationUnit | null;
  // IRIs of the certifications this one covers (outgoing) / is covered by (incoming). The list
  // payload omits `coveredBy`; it is only complete on a single read.
  covers: string[];
  coveredBy: string[];
  // How many certifications this one covers in total, direct and inherited (API-computed).
  coversCount: number;
  // IRI of the platform's default certification this one is matched to (certifications only). Null
  // when not matched yet; matching is optional but enables course suggestions.
  defaultCertification: string | null;
  // Ordered by value ascending; empty for certifications.
  levels: QualificationLevel[];
  /** How many levels the ladder has, 0 not counted (see `ladderSize`): worked out once, read by every ring. */
  ladderSize: number;
  // IRIs of the groups this qualification is directly linked to.
  groups: string[];
}

/**
 * A node of the organisation's group tree, used to organise qualifications and pick a set of them
 * in one go. A qualification linked to a group belongs to that group and to every group above it,
 * so a qualification may never sit in a group and one of its ancestors or descendants at once.
 */
export interface QualificationGroup {
  id: string;
  iri: string;
  name: string;
  // IRI of the parent group; null for a root.
  parent: string | null;
  position: number;
  // IRIs of the qualifications directly linked to this group.
  qualifications: string[];
  // Distinct qualifications in this group and everything below it (API-computed).
  qualificationCount: number;
  childCount: number;
  /** Picked in the function matrix's group filter when it is opened without one. */
  defaultForFunctionMatrix: boolean;
  /** The same for the member qualification matrix. */
  defaultForQualificationMatrix: boolean;
}

/** A group with its place in the tree — what the screens render. */
export interface QualificationGroupNode {
  group: QualificationGroup;
  depth: number;
  children: QualificationGroupNode[];
}

/**
 * A per-organisation preset of levels. Applying one to a new knowledge / skill qualification
 * copies its levels (without ids); the qualification keeps its own copy afterwards. At most one
 * scale per organisation is the default — the one a new qualification starts from.
 */
export interface QualificationScale {
  id: string;
  iri: string;
  name: string;
  isDefault: boolean;
  levels: QualificationLevel[];
  /** How many levels the scale has, 0 not counted (see `ladderSize`). */
  ladderSize: number;
}

export interface QualificationRequirement {
  id: string;
  iri: string;
  qualification: string;
  level: string | null;
  function: string | null;
  team: string | null;
  member: string | null;
  /** A growth plan's goal can carry a date to reach it by (yyyy-MM-dd); null elsewhere. */
  deadline: string | null;
}

/**
 * Where a member's required level comes from: their function or team (the standard), their own
 * personal requirement (`member`), or a course they attend that teaches it (`course`).
 */
export type MemberQualificationSourceKind = 'function' | 'team' | 'member' | 'course' | 'growthPlan';

/** The icon per source kind, shared by the overview badges and the detail dialog. */
export const QUALIFICATION_SOURCE_ICONS: Record<MemberQualificationSourceKind, string> = {
  function: 'work',
  team: 'groups',
  member: 'person',
  course: 'school',
  growthPlan: 'flag',
};

export interface MemberQualificationSource {
  kind: MemberQualificationSourceKind;
  id: string;
  name: string;
  levelValue: number | null;
  requirementId: string | null;
  /**
   * False for a function the member has left or has not started yet, and always false for a growth
   * plan (a goal, never an obligation); team, personal and course sources are always active. An
   * inactive source never raises what the member must reach.
   */
  active: boolean;
}

/**
 * One row of a member's qualification overview: what is required of them (the highest level over
 * function, team and personal requirements), what they have attained, and the certificate that
 * proves a certification. Read-only, computed live by the API.
 */
/**
 * How a member stands on a qualification, as the API judges it: held (`valid`, `expiring`, `expired`),
 * short of it (`level_not_met`, `missing_certificate`, `awaiting_confirmation`, `scheduled`, `absent`,
 * `missing`) or not applicable (`none`). `absent`: the course teaching it is over, and the member
 * was not there.
 */
export type MemberQualificationHoldStatus =
  | 'valid' | 'processed' | 'covered' | 'expiring' | 'expired'
  | 'level_not_met' | 'missing_certificate' | 'awaiting_confirmation' | 'scheduled' | 'absent' | 'missing' | 'none';

export const MEMBER_QUALIFICATION_STATUSES: MemberQualificationHoldStatus[] = ['valid', 'processed', 'covered', 'expiring', 'expired', 'level_not_met', 'missing_certificate', 'awaiting_confirmation', 'scheduled', 'absent', 'missing', 'none'];

/**
 * Held, but never earned as itself: a certificate for a certification that covers this one proves it.
 * It reads as valid — the plus badge on the status says where it came from, the label names it.
 */
export const isCoveredStatus = (status: MemberQualificationHoldStatus): boolean => status === 'covered';

export interface MemberQualification {
  qualificationId: string;
  code: string;
  name: string;
  type: QualificationType;
  certificateRequired: boolean;
  /** The member's stored record (deadline + confirmations); null until one is created. */
  memberQualificationId: string | null;
  required: {
    levelId: string | null;
    levelValue: number | null;
    levelName: string | null;
    sources: MemberQualificationSource[];
    // A personal requirement decided the level, higher or lower than team/function.
    overridden: boolean;
    /**
     * False when only functions the member no longer holds ask for it: the row is history, counts
     * against nobody (status `none`, `meetsRequirement` null) and is read as "not required".
     */
    active: boolean;
  } | null;
  /** The level of the most recent confirmation (levelId null = level 0), or null when never assessed. */
  attained: {
    levelId: string | null;
    levelValue: number | null;
    levelName: string | null;
    confirmedAt: string | null;
    confirmationCount: number;
  } | null;
  /** How many of the member's certificates prove it (directly or through a covering certification). */
  certificateCount: number;
  /**
   * The certification it is held through, where it is held that way: its own fact, so the badge
   * stands whatever the status says — expiring, waiting on a certificate, or past its date.
   */
  coveredByQualificationId: string | null;
  /** The row's date, for every type: the last confirmation, or a certification's certificate issue date. */
  lastConfirmedAt: string | null;
  /** By when the member must meet the requirement; stored on the member's record. */
  deadline: string | null;
  /** Computed by the API: the proving certificate's date, or last confirmation + the qualification's period. */
  expirationDate: string | null;
  status: MemberQualificationHoldStatus;
  /** The API's sentence for the status ("Behaald op …, geldig tot …"), already translated. */
  statusLabel: string;
  /** A planned course teaching it, when one is scheduled. */
  scheduledDate: string | null;
  /** Null when nothing is required of the member. */
  meetsRequirement: boolean | null;
  personalRequirementId: string | null;
  removable: boolean;
  /** Size of the ladder (knowledge/skill), so the ring can show the whole growth path. */
  levelCount: number | null;
  maxLevelValue: number | null;
  /** The qualification's levels, lowest first; the options when the member's own required level is changed. */
  qualificationLevels: QualificationLevel[];
}

/** One entry of a member's confirmation history; the first (most recent) is the current level. */
export interface MemberQualificationConfirmation {
  id: string;
  levelId: string | null;
  levelValue: number | null;
  levelName: string | null;
  confirmedAt: string | null;
  /** Until when this assessment stays good; null = confirmedAt + the qualification's period. */
  expirationDate: string | null;
  remarks: string | null;
  confirmedByName: string | null;
}

/** A level as offered to the confirm screen: the scale's wording plus this qualification's own context. */
export interface MemberQualificationLevelOption {
  id: string;
  value: number;
  name: string | null;
  description: string | null;
  scaleDescription: string | null;
}

/** The detail behind one row of the overview: everything needed to confirm or set a level. */
export interface MemberQualificationDetail extends MemberQualification {
  member: { id: string; firstName: string; lastName: string };
  description: string | null;
  expirationValue: number | null;
  expirationUnit: ExpirationUnit | null;
  levels: MemberQualificationLevelOption[];
  confirmations: MemberQualificationConfirmation[];
  /** The member's certificates proving it (directly or through a covering certification). */
  certificates: LearningPlanCourseCertificateListItem[];
}

/**
 * How urgent a member's deadline is, with the same colours as the expiration date: `valid` (green)
 * while it is more than 30 days away or the requirement is already met, `expiring` (orange) within
 * 30 days, `expired` (red) once passed without meeting the requirement. Null without a deadline.
 */
export function deadlineUrgency(deadline: string | null, meetsRequirement: boolean | null): 'valid' | 'expiring' | 'expired' | null {
  if (!deadline) return null;
  if (meetsRequirement === true) return 'valid';
  const due = new Date(deadline);
  due.setHours(23, 59, 59, 999);
  const msLeft = due.getTime() - Date.now();
  if (msLeft < 0) return 'expired';
  return msLeft <= 30 * 24 * 60 * 60 * 1000 ? 'expiring' : 'valid';
}

/**
 * The status badge's icon, in the certification matrix's language: green check when held, orange
 * alarm when running out, red warning when expired, a certificate or trend for what is still short
 * of the requirement, a calendar for a planned course, and a dash when nothing applies.
 */
export function qualificationStatusIcon(status: MemberQualificationHoldStatus, type?: QualificationType): string {
  switch (status) {
    // Covered and processed both read as held: the plus badge says the first came through another
    // certification, and the colour says the second was settled by hand.
    case 'valid':
    case 'processed':
    case 'covered': return 'check';
    // Running out and run out are the same issue, an expiry date: the same clock, told apart by colour.
    case 'expiring':
    case 'expired': return 'alarm';
    case 'scheduled': return 'event';
    // The planned course came and went without them: the calendar again, crossed out.
    case 'absent': return 'event_busy';
    case 'missing_certificate': return 'workspace_premium';
    case 'level_not_met': return 'trending_up';
    // A certification is held by its certificate, so what a passed course leaves waiting is that
    // certificate — the same icon as a missing one, because it is the same thing to go and do.
    case 'awaiting_confirmation': return type === 'certification' ? 'workspace_premium' : 'warning';
    case 'missing': return 'warning';
    default: return 'remove';
  }
}

export type LevelRingSegment ='ok' | 'over' | 'missing' | 'required' | 'empty';

/**
 * The conic-gradient of a level ring: one segment per level, level 1 at the top going clockwise.
 * In `attained` mode the member's level is read against the required one (green = held and
 * needed, dark green = held beyond the need, red = needed but missing, light grey = the rest of
 * the ladder). In `required` mode only the required part is marked, in grey. Colours come from
 * the `--seg-*` custom properties of the global `.level-ring` style.
 */
/** `TranslateService.instant`, passed in so these helpers stay plain functions. */
export type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

/**
 * The status badge's colour: green when held, red once expired, muted when nothing applies, and for
 * everything the member still falls short of orange while there is time, red once the deadline passed.
 */
export function qualificationStatusColor(row: MemberQualification): string {
  // Required and simply not there: red, like an expired one. For a certification the certificate is
  // the whole of it, so one without its certificate counts the same; for a level it means the level
  // is met and only its proof is to come, which stays orange. With a course already planned the
  // gap is being closed, so orange will do there too.
  const missing = row.status === 'missing' || (row.status === 'missing_certificate' && row.type === 'certification');
  if (missing) return row.scheduledDate ? 'alert' : 'warning';
  switch (row.status) {
    case 'valid': return 'success';
    // Settled by hand on the certificate ("verwerkt"): held, so the same tick, in the accent colour
    // that marks everything decided rather than earned.
    case 'processed': return 'accent';
    // Held, but through another certification: accent rather than green, so it stands out from
    // the ones the member earned themselves. The plus badge says the same thing up close.
    case 'covered': return 'accent';
    case 'expired': return 'warning';
    // A course missed: orange, it is to be planned again.
    case 'absent': return 'alert';
    case 'none': return 'disable';
    default: return deadlineUrgency(row.deadline, row.meetsRequirement) === 'expired' ? 'warning' : row.status === 'scheduled' ? 'accent' : 'alert';
  }
}

/**
 * The requirement that decides the level: a personal one always wins, otherwise the source asking
 * the required level does. When several ask the same, a function or team goes before a course: the
 * course is the exception to the standard requirements, so it only decides when it asks more.
 * One rule for the passport's badge and tooltip and the dialog's sources, so they never disagree.
 */
export function decidingSource(sources: MemberQualificationSource[], requiredLevelValue: number | null, personalRequirementId: string | null | undefined): MemberQualificationSource | null {
  if (!sources.length) return null;
  const personal = sources.find(s => s.kind === 'member') ?? null;
  if (personalRequirementId && personal) return personal;
  const courseLast = (s: MemberQualificationSource) => s.kind === 'course' ? 1 : 0;
  const inherited = sources.filter(s => s.kind !== 'member')
    .sort((a, b) => (b.levelValue ?? 0) - (a.levelValue ?? 0) || courseLast(a) - courseLast(b));
  return inherited.find(s => s.levelValue === requiredLevelValue) ?? inherited[0] ?? personal;
}

/**
 * On a required ring: what asks for it — the level itself is on the ring — or that nothing does.
 * Shared so the passport, its dialog and the growth plans all say the same thing.
 */
export function requiredSourceTooltip(row: MemberQualification, sources: MemberQualificationSource[], t: TranslateFn): string {
  const source = decidingSource(sources, row.required?.levelValue ?? null, row.personalRequirementId);
  if (!source) return t('ows.member.qualifications.detail.not-required');
  return t('ows.member.qualifications.source-' + source.kind + '-required', { name: source.name });
}

/**
 * On an attained ring: the level the member holds (a certification: what the API says of their
 * certificate), or the one line that says they do not hold it yet.
 */
export function attainedTooltip(row: MemberQualification | undefined, t: TranslateFn): string {
  const nothingYet = () => t('ows.member.qualifications.detail.level-zero');
  if (!row) return nothingYet();
  // A certification says how it stands in the API's own words, held or not: "the member does not
  // master this yet" reads as a statement about a level, which a certification does not have.
  if (row.type === 'certification') {
    if (row.statusLabel) return row.statusLabel;
    return row.certificateCount ? t('ows.member.qualifications.detail.certificate') : nothingYet();
  }
  const attained = row.attained;
  if (!attained?.levelValue) return nothingYet();
  return attained.levelName || t('ows.qualifications.level-placeholder', { value: attained.levelValue });
}

/**
 * How many levels a ladder has. Level 0 is where everyone starts rather than a level of its own,
 * so a 0-5 ladder is five: the count a scale shows and the number of segments a ring is cut in.
 */
export function ladderSize(levels: { value: number | string }[] | null | undefined): number {
  return (levels ?? []).filter(level => Number(level.value) > 0).length;
}

/**
 * Where a new requirement or goal starts: level 1, the lowest level above 0. Level 0 is where
 * everyone already stands, so asking for it asks nothing; a ladder without levels above 0 falls
 * back to its lowest.
 */
export function startLevel<T extends { value: number }>(levels: T[] | null | undefined): T | null {
  let start: T | null = null;
  let lowest: T | null = null;
  for (const level of levels ?? []) {
    if (!lowest || level.value < lowest.value) lowest = level;
    if (level.value > 0 && (!start || level.value < start.value)) start = level;
  }
  return start ?? lowest;
}

export function levelRingGradient(total: number | null, attained: number | null, required: number | null, mode: 'attained' | 'required' = 'attained'): string {
  const held = attained ?? 0;
  const needed = required ?? 0;
  const count = Math.max(total ?? 0, held, needed, 1);
  const size = 360 / count;
  // The separator is an angle, so it shrinks with the ring: on the small 32px one 4deg came out at
  // about a pixel, too thin to read the segments apart. 8deg is roughly two there, and a quarter of
  // a segment is the ceiling, so a long ladder keeps more colour than gap.
  const gap = count > 1 ? Math.min(8, size / 4) : 0;
  const stops: string[] = [];
  for (let i = 0; i < count; i++) {
    const level = i + 1;
    let segment: LevelRingSegment;
    if (mode === 'required') segment = level <= needed ? 'required' : 'empty';
    // Held above what is asked reads darker, a requirement of 0 included (the member's own, which
    // asks nothing); with no requirement at all there is nothing to be above.
    else if (level <= held) segment = required !== null && level > needed ? 'over' : 'ok';
    else segment = level <= needed ? 'missing' : 'empty';
    const start = i * size + gap / 2;
    const end = (i + 1) * size - gap / 2;
    stops.push(`transparent ${i * size}deg ${start}deg`, `var(--seg-${segment}) ${start}deg ${end}deg`, `transparent ${end}deg ${(i + 1) * size}deg`);
  }
  return `conic-gradient(${stops.join(', ')})`;
}

export const qualificationIri = (id: string) => `/qualifications/${id}`;
export const qualificationLevelIri = (id: string) => `/qualification_levels/${id}`;
export const defaultCertificationIri = (id: string) => `/default_certifications/${id}`;

/** Last path segment of an IRI, or null. */
export const iriId = (iri: string | null | undefined): string | null => iri ? String(iri).split('/').pop() ?? null : null;

const asIri = (value: any): string | null => value == null ? null : typeof value === 'string' ? value : value['@id'] ?? null;

@Injectable({
  providedIn: 'root'
})
export class QualificationLevelAdapter implements Adapter<QualificationLevel> {
  adapt(item: any): QualificationLevel {
    return {
      id: item.id ?? iriId(item['@id']) ?? '',
      iri: item['@id'] ?? qualificationLevelIri(item.id),
      value: Number(item.value),
      name: item.name ?? null,
      description: item.description ?? null,
      scaleLevel: asIri(item.scaleLevel),
      scaleDescription: item.scaleDescription ?? null,
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class QualificationAdapter implements Adapter<Qualification> {
  constructor(private levelAdapter: QualificationLevelAdapter) {}

  adapt(item: any): Qualification {
    const levels: QualificationLevel[] = (Array.isArray(item.levels) ? item.levels : []).map((l: any) => this.levelAdapter.adapt(l));
    levels.sort((a, b) => a.value - b.value);
    return {
      id: item.id,
      iri: item['@id'] ?? qualificationIri(item.id),
      organisationProfile: asIri(item.organisationProfile) ?? '',
      type: item.type,
      code: item.code ?? item.reference ?? '',
      name: item.name ?? '',
      description: item.description ?? null,
      certificateRequired: item.type === 'certification' ? true : !!item.certificateRequired,
      scale: asIri(item.scale),
      expirationValue: item.expirationValue ?? null,
      expirationUnit: item.expirationUnit ?? null,
      covers: (Array.isArray(item.covers) ? item.covers : []).map(asIri).filter((x: string | null): x is string => !!x),
      coveredBy: (Array.isArray(item.coveredBy) ? item.coveredBy : []).map(asIri).filter((x: string | null): x is string => !!x),
      coversCount: Number(item.coversCount ?? (Array.isArray(item.covers) ? item.covers.length : 0)),
      defaultCertification: asIri(item.defaultCertification),
      levels,
      ladderSize: ladderSize(levels),
      groups: (Array.isArray(item.groups) ? item.groups : []).map(asIri).filter((x: string | null): x is string => !!x),
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class QualificationGroupAdapter implements Adapter<QualificationGroup> {
  adapt(item: any): QualificationGroup {
    return {
      id: item.id ?? iriId(item['@id']) ?? '',
      iri: item['@id'] ?? `/qualification_groups/${item.id}`,
      name: item.name ?? '',
      parent: asIri(item.parent),
      position: Number(item.position ?? 0),
      qualifications: (Array.isArray(item.qualifications) ? item.qualifications : []).map(asIri).filter((x: string | null): x is string => !!x),
      qualificationCount: Number(item.qualificationCount ?? 0),
      childCount: Number(item.childCount ?? 0),
      defaultForFunctionMatrix: !!item.defaultForFunctionMatrix,
      defaultForQualificationMatrix: !!item.defaultForQualificationMatrix,
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class MemberQualificationAdapter implements Adapter<MemberQualification> {
  constructor(private levelAdapter: QualificationLevelAdapter) {}

  adapt(item: any): MemberQualification {
    // The qualification comes embedded; older payloads carried its fields flat on the row.
    const qualification = item.qualification && typeof item.qualification === 'object' ? item.qualification : item;
    const required = item.required ?? null;
    const requiredLevel = required?.level ?? null;
    const currentLevel = item.currentLevel ?? null;
    const confirmationCount = Number(item.confirmationCount ?? item.attained?.confirmationCount ?? 0);
    const lastConfirmedAt = item.lastConfirmedAt ?? item.attained?.confirmedAt ?? null;
    const status = MEMBER_QUALIFICATION_STATUSES.includes(item.status) ? item.status : 'none';
    const levels = Array.isArray(qualification.levels) ? qualification.levels : null;
    const sources: MemberQualificationSource[] = (Array.isArray(required?.sources) ? required.sources : []).map((s: any) => ({
      kind: s.kind,
      id: s.id ?? '',
      name: s.name ?? '',
      levelValue: s.levelValue ?? null,
      requirementId: s.requirementId ?? null,
      active: s.active !== false,
    }));
    // The member's own requirement overrides what the functions and teams ask, at level 0 too: then
    // the requirement is that level, and it is in force, however the payload left level and status.
    const personal = item.personalRequirementId ? sources.find(s => s.kind === 'member' && s.levelValue !== null) ?? null : null;
    const personalLevel = personal ? (levels ?? []).find((l: any) => Number(l.value) === Number(personal.levelValue)) ?? null : null;
    return {
      qualificationId: qualification.id ?? item.qualificationId ?? item.id ?? '',
      code: qualification.code ?? '',
      name: qualification.name ?? '',
      type: qualification.type,
      certificateRequired: !!qualification.certificateRequired,
      memberQualificationId: item.memberQualificationId ?? null,
      coveredByQualificationId: item.coveredByQualificationId ?? null,
      required: required ? {
        levelId: personal ? personalLevel?.id ?? null : requiredLevel?.id ?? required.levelId ?? null,
        levelValue: personal ? Number(personal.levelValue) : requiredLevel?.value ?? required.levelValue ?? null,
        levelName: personal ? personalLevel?.name ?? null : requiredLevel?.name ?? required.levelName ?? null,
        sources,
        overridden: !!required.overridden,
        active: !!personal || required.active !== false,
      } : null,
      // Assessed at least once; no current level with confirmations means level 0.
      attained: currentLevel || confirmationCount > 0 || item.attained ? {
        levelId: currentLevel?.id ?? item.attained?.levelId ?? null,
        levelValue: currentLevel?.value ?? item.attained?.levelValue ?? null,
        levelName: currentLevel?.name ?? item.attained?.levelName ?? null,
        confirmedAt: lastConfirmedAt,
        confirmationCount,
      } : null,
      certificateCount: Number(item.certificateCount ?? (item.certificate ? 1 : 0)),
      lastConfirmedAt,
      deadline: item.deadline ?? null,
      expirationDate: item.expirationDate ?? null,
      status,
      statusLabel: item.statusLabel ?? '',
      scheduledDate: item.scheduledDate ?? null,
      meetsRequirement: typeof item.meetsRequirement === 'boolean' ? item.meetsRequirement : null,
      personalRequirementId: item.personalRequirementId ?? null,
      removable: !!item.removable,
      levelCount: levels ? ladderSize(levels) : item.levelCount ?? null,
      maxLevelValue: levels?.length ? Math.max(...levels.map((l: any) => Number(l.value))) : item.maxLevelValue ?? null,
      qualificationLevels: (levels ?? []).map((l: any) => this.levelAdapter.adapt(l)).sort((a: QualificationLevel, b: QualificationLevel) => a.value - b.value),
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class MemberQualificationDetailAdapter implements Adapter<MemberQualificationDetail> {
  constructor(private rowAdapter: MemberQualificationAdapter) {}

  adapt(item: any): MemberQualificationDetail {
    const qualification = item.qualification && typeof item.qualification === 'object' ? item.qualification : item;
    const levels: MemberQualificationLevelOption[] = (Array.isArray(qualification.levels) ? qualification.levels : Array.isArray(item.levels) ? item.levels : []).map((l: any) => ({
      id: l.id ?? iriId(l['@id']) ?? '',
      value: Number(l.value),
      name: l.name ?? null,
      description: l.description ?? null,
      scaleDescription: l.scaleDescription ?? null,
    }));
    levels.sort((a, b) => a.value - b.value);
    const levelById = new Map(levels.map(l => [l.id, l]));
    // A confirmation's level arrives as an IRI (or an embedded object, or flat fields): resolve it on the ladder.
    const confirmationLevel = (c: any): MemberQualificationLevelOption | null => {
      const ref = c.level;
      const id = typeof ref === 'string' ? iriId(ref) : ref?.id ?? c.levelId ?? null;
      return id ? levelById.get(id) ?? (typeof ref === 'object' && ref ? { id, value: Number(ref.value), name: ref.name ?? null, description: null, scaleDescription: null } : null) : null;
    };
    const row = this.rowAdapter.adapt(item);
    const profile = item.member?.userProfile ?? item.member ?? {};
    return {
      ...row,
      levelCount: ladderSize(levels),
      certificateCount: Array.isArray(item.certificates) ? item.certificates.length : row.certificateCount,      certificates: [],
      member: { id: item.member?.id ?? '', firstName: profile.firstName ?? '', lastName: profile.lastName ?? '' },
      description: qualification.description ?? item.description ?? null,
      expirationValue: qualification.expirationValue ?? item.expirationValue ?? null,
      expirationUnit: qualification.expirationUnit ?? item.expirationUnit ?? null,
      levels,
      confirmations: (Array.isArray(item.confirmations) ? item.confirmations : []).map((c: any) => {
        const level = confirmationLevel(c);
        return {
        id: c.id ?? iriId(c['@id']) ?? '',
        levelId: level?.id ?? null,
        levelValue: level?.value ?? c.levelValue ?? null,
        levelName: level?.name ?? c.levelName ?? null,
        confirmedAt: c.confirmedAt ?? null,
        remarks: c.remarks ?? null,
        confirmedByName: c.confirmedByName ?? null,
        expirationDate: c.expirationDate ?? null,
        };
      }),
    };
  }
}

/** Nests a flat group list by `parent`; siblings ordered by position, then name. Orphans become roots. */
export function buildGroupTree(groups: QualificationGroup[]): QualificationGroupNode[] {
  const byIri = new Set(groups.map(g => g.iri));
  const byParent = new Map<string | null, QualificationGroup[]>();
  groups.forEach(g => {
    const key = g.parent && byIri.has(g.parent) ? g.parent : null;
    byParent.set(key, [...(byParent.get(key) ?? []), g]);
  });
  const order = (a: QualificationGroup, b: QualificationGroup) => a.position - b.position || a.name.localeCompare(b.name);
  const build = (parent: string | null, depth: number): QualificationGroupNode[] =>
    (byParent.get(parent) ?? []).sort(order).map(group => ({ group, depth, children: build(group.iri, depth + 1) }));
  return build(null, 0);
}

/** Depth-first flattening of the tree, parents before their children. */
export function flattenGroupTree(nodes: QualificationGroupNode[]): QualificationGroupNode[] {
  return nodes.flatMap(node => [node, ...flattenGroupTree(node.children)]);
}

@Injectable({
  providedIn: 'root'
})
export class QualificationScaleAdapter implements Adapter<QualificationScale> {
  constructor(private levelAdapter: QualificationLevelAdapter) {}

  adapt(item: any): QualificationScale {
    const levels: QualificationLevel[] = (Array.isArray(item.levels) ? item.levels : []).map((l: any) => ({
      ...this.levelAdapter.adapt(l),
      iri: l['@id'] ?? `/qualification_scale_levels/${l.id}`,
    }));
    levels.sort((a, b) => a.value - b.value);
    return {
      id: item.id ?? iriId(item['@id']) ?? '',
      iri: item['@id'] ?? `/qualification_scales/${item.id}`,
      name: item.name ?? '',
      isDefault: !!(item.isDefault ?? item.default),
      levels,
      ladderSize: ladderSize(levels),
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class QualificationRequirementAdapter implements Adapter<QualificationRequirement> {
  adapt(item: any): QualificationRequirement {
    return {
      id: item.id ?? iriId(item['@id']) ?? '',
      iri: item['@id'] ?? `/qualification_requirements/${item.id}`,
      qualification: asIri(item.qualification) ?? '',
      level: asIri(item.level),
      function: asIri(item.function),
      team: asIri(item.team),
      member: asIri(item.member),
      // The API may send a full datetime; the screens compare and send the date only.
      deadline: item.deadline ? String(item.deadline).slice(0, 10) : null,
    };
  }
}

/** Index a catalogue by IRI — the key every link and requirement references. */
export function indexQualificationsByIri(items: Qualification[]): Map<string, Qualification> {
  const byIri = new Map<string, Qualification>();
  items.forEach(q => byIri.set(q.iri, q));
  return byIri;
}

/**
 * Every certification reachable from `iri` by following `covers` (descendants), excluding itself.
 * Diamonds are possible, hence the `seen` guard.
 */
export function coveredDescendants(iri: string, byIri: Map<string, Qualification>): Set<string> {
  const seen = new Set<string>();
  const stack = [...(byIri.get(iri)?.covers ?? [])];
  while (stack.length) {
    const next = stack.pop()!;
    if (next === iri || seen.has(next) || !byIri.has(next)) continue;
    seen.add(next);
    stack.push(...byIri.get(next)!.covers);
  }
  return seen;
}

/**
 * Every certification that (transitively) covers `iri` (ancestors). The list payload has no
 * `coveredBy`, so the incoming edges are derived by inverting `covers` over the whole catalogue.
 */
export function coveringAncestors(iri: string, byIri: Map<string, Qualification>): Set<string> {
  const parents = new Map<string, string[]>();
  byIri.forEach(q => q.covers.forEach(child => parents.set(child, [...(parents.get(child) ?? []), q.iri])));
  const seen = new Set<string>();
  const stack = [...(parents.get(iri) ?? [])];
  while (stack.length) {
    const next = stack.pop()!;
    if (next === iri || seen.has(next)) continue;
    seen.add(next);
    stack.push(...(parents.get(next) ?? []));
  }
  return seen;
}
